import apiConfig from "../ApiConfig";

const BASE = "/api/tprm";

const appendParams = (params, values, allowedKeys) => {
  allowedKeys.forEach((key) => {
    const value = values[key];
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
};

const getResourceList = async (resource, filters, allowedKeys) => {
  const params = new URLSearchParams();
  appendParams(params, filters, allowedKeys);
  const query = params.toString();
  const response = await apiConfig.get(
    `${BASE}/${resource}/${query ? `?${query}` : ""}`,
  );
  return response.data;
};

const postResourceAction = async (resource, id, action, payload = {}) => {
  const response = await apiConfig.post(
    `${BASE}/${resource}/${encodeURIComponent(id)}/${action}/`,
    payload,
  );
  return response.data;
};

export const getTPRMDashboardStatsApi = async () => {
  const response = await apiConfig.get(`${BASE}/vendors/dashboard_stats/`);
  return response.data;
};

export const getTPRMPlatformDashboardApi = async () => {
  const response = await apiConfig.get(`${BASE}/vendors/platform_dashboard/`);
  return response.data;
};

export const getTPRMPlatformVendorsApi = async (filters = {}) => {
  const params = new URLSearchParams();
  appendParams(params, filters, [
    "customer",
    "vendor_name",
    "industry",
    "criticality",
    "risk_tier",
    "status",
    "created_from",
    "created_to",
    "limit",
    "offset",
  ]);
  const query = params.toString();
  const response = await apiConfig.get(
    `${BASE}/vendors/platform_list/${query ? `?${query}` : ""}`,
  );
  return response.data;
};

export const getTPRMVendorsApi = async (filters = {}) => {
  const params = new URLSearchParams();
  appendParams(params, filters, [
    "search",
    "status",
    "risk_level",
    "limit",
    "offset",
    "tenant_id",
  ]);
  const query = params.toString();
  const response = await apiConfig.get(
    `${BASE}/vendors/${query ? `?${query}` : ""}`,
  );
  return response.data;
};

export const getTPRMAssessmentsApi = (filters = {}) => {
  const params = new URLSearchParams();
  appendParams(params, filters, ["limit", "offset", "vendor_id", "status"]);
  const query = params.toString();
  return apiConfig
    .get(`${BASE}/assessments/${query ? `?${query}` : ""}`)
    .then((response) => response.data);
};

export const getTPRMResponsesApi = (filters = {}) =>
  getResourceList("assessments/responses", filters, [
    "limit",
    "offset",
    "vendor_id",
    "status",
    "criticality",
    "risk_tier",
    "date_from",
    "date_to",
    "completion",
    "search",
  ]);

export const getTPRMDomainScansApi = (filters = {}) =>
  getResourceList("domain-scans", filters, [
    "limit",
    "offset",
    "tenant_id",
    "scan_id",
    "vendor_id",
    "service_id",
    "status",
    "hostname",
  ]);

export const getTPRMDomainScanApi = async (scanId, tenantId) => {
  const params = new URLSearchParams();
  appendParams(params, { tenant_id: tenantId }, ["tenant_id"]);
  const query = params.toString();
  const response = await apiConfig.get(
    `${BASE}/domain-scans/${encodeURIComponent(scanId)}/${query ? `?${query}` : ""}`,
  );
  return response.data;
};

export const createTPRMDomainScanApi = async (payload) => {
  const response = await apiConfig.post(`${BASE}/domain-scans/`, payload);
  return response.data;
};

export const getTPRMDomainFindingsApi = (filters = {}) =>
  getResourceList("domain-scan-findings", filters, [
    "limit",
    "offset",
    "vendor_id",
    "service_id",
    "severity",
    "hostname",
    "tenant_id",
  ]);

export const promoteTPRMDomainFindingApi = (findingId, tenantId) => {
  const params = new URLSearchParams();
  appendParams(params, { tenant_id: tenantId }, ["tenant_id"]);
  const query = params.toString();
  return apiConfig.post(
    `${BASE}/domain-scan-findings/${encodeURIComponent(findingId)}/promote/${query ? `?${query}` : ""}`,
    {},
  ).then((response) => response.data);
};

export const getTPRMResponseDetailApi = async (assessmentId) => {
  const response = await apiConfig.get(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/response-detail/`,
  );
  return response.data;
};

export const approveTPRMResponseApi = (assessmentId, comments = "") =>
  postResourceAction("assessments", assessmentId, "approve", { comments });

export const requestTPRMResponseChangesApi = (assessmentId, reason) =>
  postResourceAction("assessments", assessmentId, "reject", { reason });

export const getTPRMVendorServicesApi = (filters = {}) =>
  getResourceList("vendor-services", filters, [
    "limit",
    "offset",
    "vendor_id",
    "status",
  ]);

export const getTPRMFindingsApi = (filters = {}) =>
  getResourceList("findings", filters, [
    "limit",
    "offset",
    "finding_id",
    "vendor_id",
    "severity",
    "status",
  ]);

export const tprmFindingActionApi = (findingId, action) =>
  postResourceAction("findings", findingId, action);

export const getTPRMApprovalsApi = (filters = {}) =>
  getResourceList("approvals", filters, ["limit", "offset", "decision"]);

export const decideTPRMApprovalApi = (approvalId, payload) =>
  postResourceAction("approvals", approvalId, "decide", payload);

export const verifyTPRMApprovalConditionApi = (approvalId, conditionIndex) =>
  postResourceAction("approvals", approvalId, "verify_condition", {
    condition_index: conditionIndex,
  });

export const getTPRMEvidenceApi = (filters = {}) =>
  getResourceList("evidence", filters, [
    "limit",
    "offset",
    "vendor_id",
    "assessment_id",
    "finding_id",
  ]);

export const uploadTPRMEvidenceApi = async (formData) => {
  const response = await apiConfig.post(`${BASE}/evidence/upload/`, formData);
  return response.data;
};

export const downloadTPRMEvidenceApi = async (evidenceId) => {
  const response = await apiConfig.get(
    `${BASE}/evidence/${encodeURIComponent(evidenceId)}/download/`,
    { responseType: "blob" },
  );
  return response;
};

export const verifyTPRMEvidenceApi = (evidenceId, payload = {}) =>
  postResourceAction("evidence", evidenceId, "verify", payload);

export const rejectTPRMEvidenceApi = (evidenceId, payload) =>
  postResourceAction("evidence", evidenceId, "reject", payload);

export const getTPRMContractsApi = (filters = {}) =>
  getResourceList("contracts", filters, [
    "limit",
    "offset",
    "vendor_id",
    "status",
  ]);

export const getTPRMExpiringContractsApi = async () => {
  const response = await apiConfig.get(`${BASE}/contracts/expiring_soon/`);
  return response.data;
};

export const getTPRMRiskAcceptancesApi = (filters = {}) =>
  getResourceList("risk-acceptances", filters, [
    "limit",
    "offset",
    "status",
  ]);

export const decideTPRMRiskAcceptanceApi = (acceptanceId, payload) =>
  postResourceAction("risk-acceptances", acceptanceId, "decide", payload);

export const revokeTPRMRiskAcceptanceApi = (acceptanceId, payload = {}) =>
  postResourceAction("risk-acceptances", acceptanceId, "revoke", payload);

export const getTPRMAuditEventsApi = (filters = {}) =>
  getResourceList("audit-events", filters, [
    "limit",
    "offset",
    "resource_type",
    "resource_id",
  ]);

export const createTPRMVendorApi = async (vendor) => {
  const response = await apiConfig.post(`${BASE}/vendors/`, vendor);
  return response.data;
};

export const getTPRMSelectableTemplatesApi = async () => {
  let response = await apiConfig.get(
    `${BASE}/assessment-templates/?selectable=true`,
  );
  if (Array.isArray(response.data)) return response.data;

  const firstPage = response.data || {};
  const results = [...(firstPage.results || [])];
  let next = firstPage.next;
  while (next) {
    response = await apiConfig.get(next);
    results.push(...(response.data?.results || []));
    next = response.data?.next;
  }
  return { ...firstPage, results, next: null, previous: null };
};

export const getTPRMTemplatesApi = (pagination = {}) =>
  getResourceList("assessment-templates", pagination, [
    "limit",
    "offset",
    "is_active",
  ]);

export const getTPRMCustomerAdminOptionsApi = async () => {
  const response = await apiConfig.get(`${BASE}/customer-admin-options/`);
  return response.data;
};

export const getTPRMTemplateApi = async (templateId) => {
  const response = await apiConfig.get(
    `${BASE}/assessment-templates/${encodeURIComponent(templateId)}/`,
  );
  return response.data;
};

export const createTPRMTemplateApi = async (template) => {
  const response = await apiConfig.post(
    `${BASE}/assessment-templates/`,
    template,
  );
  return response.data;
};

export const updateTPRMTemplateApi = async (templateId, template) => {
  const response = await apiConfig.patch(
    `${BASE}/assessment-templates/${encodeURIComponent(templateId)}/`,
    template,
  );
  return response.data;
};

export const createTPRMAssessmentApi = async (assessment) => {
  const response = await apiConfig.post(`${BASE}/assessments/`, assessment);
  return response.data;
};

export const getTPRMAssessmentApi = async (assessmentId) => {
  const response = await apiConfig.get(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/`,
  );
  return response.data;
};

export const sendTPRMAssessmentApi = async (assessmentId) => {
  const response = await apiConfig.post(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/send/`,
  );
  return response.data;
};

export const getTPRMAssessmentVendorEntryApi = async (assessmentId) => {
  const response = await apiConfig.get(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/vendor-entry/`,
  );
  return response.data;
};

export const saveTPRMAssessmentVendorEntryApi = async (assessmentId, responses) => {
  const response = await apiConfig.patch(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/vendor-entry/`,
    { responses },
  );
  return response.data;
};

export const submitTPRMAssessmentApi = (assessmentId) =>
  postResourceAction("assessments", assessmentId, "submit");

export const uploadTPRMAssessmentEvidenceApi = async ({
  assessmentId,
  vendorId,
  questionId,
  file,
}) => {
  const formData = new FormData();
  formData.append("assessment_id", assessmentId);
  formData.append("vendor_id", vendorId);
  formData.append("question_id", questionId);
  formData.append("document_type", "Assessment response evidence");
  formData.append("file", file);
  const response = await apiConfig.post(`${BASE}/evidence/upload/`, formData);
  return response.data;
};

export const revokeTPRMAssessmentInvitationApi = async (assessmentId) => {
  const response = await apiConfig.post(
    `${BASE}/assessments/${encodeURIComponent(assessmentId)}/revoke_invitation/`,
  );
  return response.data;
};

const vendorInvitationRequestConfig = (invitationToken) => ({
  skipAuth: true,
  headers: { "X-TPRM-Invitation": invitationToken },
});

export const getVendorAssessmentInvitationApi = async (invitationToken) => {
  const response = await apiConfig.get(
    `${BASE}/vendor-assessments/access/`,
    vendorInvitationRequestConfig(invitationToken),
  );
  return response.data;
};

export const saveVendorAssessmentResponsesApi = async (
  invitationToken,
  responses,
) => {
  const response = await apiConfig.patch(
    `${BASE}/vendor-assessments/responses/`,
    { responses },
    vendorInvitationRequestConfig(invitationToken),
  );
  return response.data;
};

export const submitVendorAssessmentApi = async (invitationToken) => {
  const response = await apiConfig.post(
    `${BASE}/vendor-assessments/submit/`,
    {},
    vendorInvitationRequestConfig(invitationToken),
  );
  return response.data;
};

export const uploadVendorAssessmentEvidenceApi = async (
  invitationToken,
  questionId,
  file,
) => {
  const formData = new FormData();
  formData.append("question_id", questionId);
  formData.append("file", file);
  const response = await apiConfig.post(
    `${BASE}/vendor-assessments/evidence/`,
    formData,
    vendorInvitationRequestConfig(invitationToken),
  );
  return response.data;
};
